import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import '../styles/AlertHistory.css';

const alertsData = [
  { type: 'Threshold exceeded', count: 35, color: '#ffc107' },
  { type: 'Flow stopped', count: 15, color: '#e53935' },
  { type: 'Flow started', count: 12, color: '#4caf50' },
  { type: 'Valve action', count: 8, color: '#3a4250' },
  { type: 'System notification', count: 30, color: '#2196f3' },
];

const mockAlertHistory = [
  { 
    id: 1,
    risk: 'Medium', 
    color: '#ffc107', 
    alert: 'Threshold exceeded', 
    date: '02/11/24', 
    time: '08:32 AM',
    device: 'EUI028',
    deviceType: 'water',
    message: 'Your daily consumption threshold has been exceeded by 100L', 
    sentTo: 'EMUcustomer@outlook.com.au' 
  },
  { 
    id: 2,
    risk: 'Medium', 
    color: '#ffc107', 
    alert: 'Threshold exceeded', 
    date: '03/11/24', 
    time: '09:15 AM',
    device: 'EUI029',
    deviceType: 'water',
    message: 'Your daily consumption threshold has been exceeded by 200L', 
    sentTo: 'EMUcustomer@outlook.com.au' 
  },
  { 
    id: 3,
    risk: 'High', 
    color: '#e53935', 
    alert: 'Flow stopped', 
    date: '03/11/24', 
    time: '02:20 PM',
    device: 'EUI028',
    deviceType: 'water',
    message: 'Water flow has suddenly stopped in EUI028. This could indicate a blockage or system issue.', 
    sentTo: 'EMUcustomer@outlook.com.au' 
  },
  { 
    id: 4,
    risk: 'High', 
    color: '#e53935', 
    alert: 'Threshold exceeded', 
    date: '03/11/24', 
    time: '03:45 PM',
    device: 'EUI028',
    deviceType: 'water',
    message: 'Device EUI028 has consumed 1000L compared to its average of 100L. We advise you to examine if there is a leak.', 
    sentTo: 'EMUcustomer@outlook.com.au' 
  },
  { 
    id: 5,
    risk: 'Low', 
    color: '#4caf50', 
    alert: 'Valve action', 
    date: '03/11/24', 
    time: '04:10 PM',
    device: 'EUI028',
    deviceType: 'water',
    message: 'The valve to EUI028 has now been switched off.', 
    sentTo: 'EMUcustomer@outlook.com.au' 
  },
  { 
    id: 6,
    risk: 'Low', 
    color: '#4caf50', 
    alert: 'Flow started', 
    date: '04/11/24', 
    time: '09:30 AM',
    device: 'EUI028',
    deviceType: 'water',
    message: 'Water is now flowing in EUI028 after maintenance.', 
    sentTo: 'EMUcustomer@outlook.com.au' 
  },
  { 
    id: 7,
    risk: 'Medium', 
    color: '#ffc107', 
    alert: 'System notification', 
    date: '05/11/24', 
    time: '10:15 AM',
    device: 'EUI027',
    deviceType: 'electricity',
    message: 'Electricity usage spike detected in EUI027. Usage is 30% higher than normal.', 
    sentTo: 'EMUcustomer@outlook.com.au' 
  },
  { 
    id: 8,
    risk: 'Low', 
    color: '#2196f3', 
    alert: 'System notification', 
    date: '05/11/24', 
    time: '11:00 AM',
    device: 'System',
    deviceType: 'system',
    message: 'Monthly report for October has been generated and emailed to you.', 
    sentTo: 'EMUcustomer@outlook.com.au' 
  },
];

const AlertHistory = () => {
  const [selectedRisk, setSelectedRisk] = useState('all');
  const [selectedDeviceType, setSelectedDeviceType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter alerts based on selected criteria
  const filteredAlerts = mockAlertHistory.filter(alert => {
    const matchesRisk = selectedRisk === 'all' || alert.risk === selectedRisk;
    const matchesDeviceType = selectedDeviceType === 'all' || alert.deviceType === selectedDeviceType;
    const matchesSearch = searchTerm === '' || 
      alert.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.device.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesRisk && matchesDeviceType && matchesSearch;
  });
  
  // Function to render pie chart data
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
    
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };
  
  return (
    <div className="alerthistory-page">
      <div className="alerthistory-header">
        <h1>Alert History</h1>
        <div className="alert-export">
          <button className="export-button">
            <span className="export-icon">📊</span> Export Alerts
          </button>
        </div>
      </div>
      
      <div className="alerthistory-charts-row">
        <div className="alerthistory-chart-block">
          <h3>Alert Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie 
                data={alertsData} 
                dataKey="count" 
                nameKey="type" 
                cx="50%" 
                cy="50%" 
                innerRadius={60} 
                outerRadius={90} 
                label={renderCustomizedLabel}
              >
                {alertsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend layout="vertical" verticalAlign="middle" align="right" />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="alerthistory-filters">
        <div className="filter-group">
          <input 
            type="text" 
            placeholder="Search alerts..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-row">
          <div className="filter-group">
            <label>Risk Level:</label>
            <select 
              value={selectedRisk} 
              onChange={(e) => setSelectedRisk(e.target.value)}
            >
              <option value="all">All Risks</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Device Type:</label>
            <select 
              value={selectedDeviceType} 
              onChange={(e) => setSelectedDeviceType(e.target.value)}
            >
              <option value="all">All Devices</option>
              <option value="water">Water Meters</option>
              <option value="electricity">Electricity Meters</option>
              <option value="system">System Notifications</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="alerthistory-table-container">
        <div className="alerthistory-table-block">
          <table>
            <thead>
              <tr>
                <th>Risk</th>
                <th>Alert Type</th>
                <th>Date & Time</th>
                <th>Device</th>
                <th>Alert Message</th>
                <th>Sent To</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlerts.length > 0 ? (
                filteredAlerts.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="risk-badge" style={{ background: row.color }}>
                        {row.risk}
                      </span>
                    </td>
                    <td>{row.alert}</td>
                    <td>
                      {row.date}<br />
                      <span className="alert-time">{row.time}</span>
                    </td>
                    <td>
                      {row.device}
                      {row.deviceType === 'water' && <span className="device-type-indicator water"></span>}
                      {row.deviceType === 'electricity' && <span className="device-type-indicator electricity"></span>}
                    </td>
                    <td>{row.message}</td>
                    <td>{row.sentTo}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="no-alerts-message">
                    No alerts match your search criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AlertHistory; 